import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldOff, FileText, FileCheck2, PenLine } from "lucide-react";

export default function LandingPage() {
  const { t, lang } = useLanguage();
  const l = t.landing;

  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border bg-background">
        <div className="grid-bg absolute inset-0 opacity-40" aria-hidden />
        <div className="radial-gold absolute inset-0" aria-hidden />
        <div className="grain absolute inset-0" aria-hidden />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-6 py-24 md:grid-cols-[1.2fr_1fr] md:py-32 lg:py-40">
          <div className="flex flex-col justify-center">
            <div
              data-testid="landing-overline"
              className="font-mono text-xs uppercase tracking-[0.32em] text-[hsl(var(--gold))]"
            >
              {l.overline}
            </div>
            <h1 className="font-display mt-6 text-5xl font-bold leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl">
              <span className="block">{l.title_1}</span>
              <span className="block">{l.title_2}</span>
              <span className="block italic text-[hsl(var(--gold))]">
                {l.title_3}
              </span>
            </h1>
            <div className="mt-6 h-px w-24 bg-[hsl(var(--gold))]/60" aria-hidden />
            <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
              {l.subtitle}
            </p>
            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <Link to="/advisor" data-testid="landing-cta-advisor">
                <Button
                  size="lg"
                  className="h-12 rounded-md bg-[hsl(var(--gold))] px-8 font-semibold text-[hsl(var(--primary-foreground))] shadow-lg shadow-[hsl(var(--gold))]/20 hover:bg-[hsl(var(--gold-soft))]"
                >
                  {lang === "pt" ? "Qual formulário eu preciso?" : lang === "es" ? "¿Qué formulario necesito?" : "Which form do I need?"}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/wizard" data-testid="landing-cta-start">
                <Button
                  variant="outline"
                  size="lg"
                  className="h-12 rounded-md border-[hsl(var(--gold))]/60 bg-transparent px-8 text-[hsl(var(--gold))] hover:bg-[hsl(var(--gold))]/10 hover:text-[hsl(var(--gold))]"
                >
                  {l.cta_primary}
                </Button>
              </Link>
            </div>
          </div>

          {/* Right: technical spec panel */}
          <div className="hairline relative flex flex-col justify-between overflow-hidden rounded-lg bg-card p-7 shadow-2xl backdrop-blur">
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[hsl(var(--gold))]/60 to-transparent" aria-hidden />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--gold))]">
                Supported forms
              </div>
              <ul className="mt-5 divide-y divide-border">
                {[
                  { code: "I-140", desc: "EB-1 · EB-2 NIW · EB-3" },
                  { code: "I-485", desc: "Adjustment of Status" },
                  { code: "I-765", desc: "EAD · Work Auth" },
                  { code: "I-131", desc: "Advance Parole" },
                  { code: "I-129", desc: "H-1B · L-1 · O-1 · TN" },
                  { code: "I-589", desc: "Asylum · Withholding" },
                  { code: "I-360", desc: "VAWA · SIJ · Religious" },
                  { code: "I-914", desc: "T Visa · Trafficking" },
                ].map((f) => (
                  <li
                    key={f.code}
                    className="flex items-center justify-between py-3"
                  >
                    <span className="font-display text-lg font-bold tracking-tight text-[hsl(var(--gold))]">
                      {f.code}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                      {f.desc}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-6 border-t border-border pt-5">
              <div className="flex items-center gap-2 text-[hsl(var(--warning))]">
                <ShieldOff className="h-4 w-4" strokeWidth={2.5} />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em]">
                  {l.not_doing_overline}
                </span>
              </div>
              <ul className="mt-3 space-y-1 text-sm">
                {l.not_doing_items.map((it) => (
                  <li key={it} className="text-muted-foreground">
                    — {it}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section className="relative border-b border-border bg-background">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mb-10 grid gap-8 md:grid-cols-[1.2fr_1fr]">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--gold))]">
                Combo Packages
              </div>
              <h2 className="font-display mt-3 text-4xl font-bold tracking-tight sm:text-5xl">
                {t.packages.title}
              </h2>
              <div className="mt-4 h-px w-16 bg-[hsl(var(--gold))]/60" aria-hidden />
              <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground">
                {t.packages.subtitle}
              </p>
              <div className="mt-6">
                <Link to="/packages" data-testid="landing-packages-cta">
                  <Button
                    size="lg"
                    className="h-12 rounded-md bg-[hsl(var(--gold))] px-8 font-semibold text-[hsl(var(--primary-foreground))] shadow-lg shadow-[hsl(var(--gold))]/20 hover:bg-[hsl(var(--gold-soft))]"
                  >
                    {t.packages.create_cta}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hairline relative overflow-hidden rounded-lg bg-card p-6 shadow-xl">
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[hsl(var(--gold))]/50 to-transparent" aria-hidden />
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
                AOS Combo (EB-2 NIW)
              </div>
              <ul className="mt-4 space-y-2">
                {[
                  { code: "I-140", note: "EB-2 NIW Petition" },
                  { code: "I-485", note: "Adjustment of Status" },
                  { code: "I-765", note: "EAD c(9)" },
                  { code: "I-131", note: "Advance Parole" },
                ].map((f) => (
                  <li
                    key={f.code}
                    className="flex items-center justify-between border-b border-border py-2 last:border-0"
                  >
                    <span className="font-display text-lg font-bold tracking-tight text-[hsl(var(--gold))]">
                      {f.code}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      {f.note}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-b border-border bg-card">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mb-10 flex items-end justify-between">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--gold))]">
                Workflow
              </div>
              <h2 className="font-display mt-3 text-4xl font-bold tracking-tight sm:text-5xl">
                {l.how_it_works}
              </h2>
              <div className="mt-4 h-px w-16 bg-[hsl(var(--gold))]/60" aria-hidden />
            </div>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            <StepCard
              icon={<FileText className="h-5 w-5" strokeWidth={2.2} />}
              title={l.step_1_title}
              desc={l.step_1_desc}
            />
            <StepCard
              icon={<PenLine className="h-5 w-5" strokeWidth={2.2} />}
              title={l.step_2_title}
              desc={l.step_2_desc}
            />
            <StepCard
              icon={<FileCheck2 className="h-5 w-5" strokeWidth={2.2} />}
              title={l.step_3_title}
              desc={l.step_3_desc}
            />
          </div>
        </div>
      </section>
    </div>
  );
}

function StepCard({ icon, title, desc }) {
  return (
    <div className="hairline group relative flex flex-col gap-4 overflow-hidden rounded-lg bg-background p-6 transition-all hover:border-[hsl(var(--gold))]/50 hover:shadow-lg hover:shadow-[hsl(var(--gold))]/5">
      <div className="flex h-11 w-11 items-center justify-center rounded-md border border-[hsl(var(--gold))]/30 bg-[hsl(var(--gold))]/10 text-[hsl(var(--gold))]">
        {icon}
      </div>
      <div className="font-display text-xl font-bold leading-snug">{title}</div>
      <p className="text-sm leading-relaxed text-muted-foreground">{desc}</p>
    </div>
  );
}
